import { isLevel, isSupportedLanguage } from "@lingoshade/domain";
import {
  createSlice,
  createAsyncThunk,
  type PayloadAction,
} from "@reduxjs/toolkit";

import i18n from "../../../config/i18n";
import { AVAILABLE_LEVELS, TEXT_SIZES } from "../../../constants/filters";
// Keep using old API interface temporarily to avoid breaking changes
// Partial migration: start moving off legacy compatibility adapter to shared typed API
// Legacy api dependency removed: all operations now use sharedApi
import { sharedApi, getAuthToken } from "../../../services/sharedApi";
import { submitRating as submitPracticeRating } from "../../../store/slices/practiceSlice";
import { toApiError } from "../../../utils/errorUtils";

import type {
  TextListItem,
  TextFilters as SharedTextFilters,
} from "@lingoshade/api";

// Local legacy Text shape retained temporarily; will be replaced by @lingoshade/api models
export interface Text {
  id: string;
  content: string;
  translation?: string | null;
  language: string;
  level: string;
  textSize: string;
  topic?: string | null;
  difficulty?: number | null;
  voices: unknown[];
  averageRating: number;
  userRating: number;
  isFavorited: boolean;
  isPublic: boolean;
  isPrivate: boolean;
  author: { id: string; firstName: string | null; lastName: string | null };
  createdAt: string;
  updatedAt: string;
}
export interface GenerateTextRequest {
  method?: "AI" | "OWN_TEXT";
  content?: string;
  title?: string;
  topic: string;
  level: string;
  language: string;
  textSize: string; // SHORT | MEDIUM | LONG
}

// Types
export interface TextsFilters {
  language?: string;
  level?: string;
  topic?: string;
  textSize?: string;
  search?: string;
  sortOrder?: "asc" | "desc";
}

export interface TextsPagination {
  offset: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

export interface FilteredTextsState {
  requestId: string | null;
  texts: Text[];
  pagination: TextsPagination;
  loadCount: number;
  hasMore: boolean;
}

export interface TextsState {
  mineFilters: { language: string; level: string };
  // Separate text storage for each filter
  textsByFilter: {
    all: FilteredTextsState;
    mine: FilteredTextsState;
    favorites: FilteredTextsState;
  };
  filters: TextsFilters;
  loading: boolean;
  error: string | null;

  // UI state
  activeFilter: "all" | "mine" | "favorites";
  searchTerm: string;
  isRandomMode: boolean;

  // Modal states
  generateDialogOpen: boolean;
  generateLoading: boolean;
  generateError: string;

  // Generation form
  generateForm: {
    method?: "AI" | "OWN_TEXT";
    content?: string;
    title?: string;
    language?: string;
    level?: string;
    contentPrompt: string;
    textSize: "SHORT" | "MEDIUM" | "LONG";
  };

  // Filter options
  filterOptions: {
    languages: string[];
    levels: string[];
    topics: string[];
    textSizes: string[];
  };
}

const initialFilterState: FilteredTextsState = {
  requestId: null,
  texts: [],
  pagination: {
    offset: 0,
    limit: 12,
    total: 0,
    hasMore: false,
  },
  loadCount: 0,
  hasMore: false,
};

const initialState: TextsState = {
  mineFilters: { language: "", level: "" },
  textsByFilter: {
    all: { ...initialFilterState },
    mine: { ...initialFilterState },
    favorites: { ...initialFilterState },
  },
  filters: {
    sortOrder: "desc",
  },
  loading: false,
  error: null,

  // UI state
  activeFilter: "all",
  searchTerm: "",
  isRandomMode: false,

  // Modal states
  generateDialogOpen: false,
  generateLoading: false,
  generateError: "",

  // Generation form
  generateForm: {
    contentPrompt: "",
    textSize: "SHORT",
  },

  // Filter options - now using constants instead of API
  filterOptions: {
    languages: [] as string[], // Will be set from user profile/session
    levels: [...AVAILABLE_LEVELS] as string[],
    topics: [] as string[], // Not used for filtering
    textSizes: [...TEXT_SIZES] as string[],
  },
};

// Async thunks
interface FetchTextsParams {
  filters?: Partial<TextsFilters>;
  loadMore?: boolean;
  filterType?: "all" | "mine" | "favorites";
}

interface FetchTextsResponse {
  texts: Text[];
  pagination: TextsPagination;
  append?: boolean;
  filterType: "all" | "mine" | "favorites";
}

// Fetch texts with filters and pagination
export const fetchTexts = createAsyncThunk<
  FetchTextsResponse,
  FetchTextsParams,
  { rejectValue: string; state: { texts: TextsState } }
>(
  "texts/fetchTexts",
  async (
    { filters = {}, loadMore = false, filterType = "all" },
    { rejectWithValue, getState },
  ) => {
    try {
      const state = getState();
      const currentFilters =
        filterType === "mine"
          ? {
              ...state.texts.mineFilters,
              search: state.texts.searchTerm,
              ...filters,
            }
          : { ...state.texts.filters, ...filters };

      const currentFilterState = state.texts.textsByFilter[filterType];
      const currentTexts = currentFilterState.texts;

      // Build new shared API filters
      const sharedFilters: SharedTextFilters = {
        offset: loadMore ? currentTexts.length : 0,
        limit: 12,
        language: currentFilters.language || undefined,
        level: currentFilters.level || undefined,
        search:
          currentFilters.search && currentFilters.search.length >= 3
            ? currentFilters.search
            : undefined,
      };

      switch (filterType) {
        case "favorites":
          sharedFilters.filter = "favorites";
          break;
        case "mine":
          sharedFilters.filter = "mine";
          break;
        case "all":
        default:
          sharedFilters.filter = "all";
          break;
      }

      const token = getAuthToken();
      const response = await sharedApi.texts.list(sharedFilters, token);

      // Map response to internal format
      const mapped: Text[] = response.texts.map(
        (item: TextListItem): Text => ({
          id: item.id,
          content: item.content,
          translation: undefined,
          language: item.language,
          level: item.level,
          textSize: item.textSize || "MEDIUM",
          topic: item.topic || undefined,
          difficulty: item.difficulty,
          voices: [],
          averageRating: item.averageRating || 0,
          userRating: item.userRating || 0,
          isFavorited: item.isFavorited || false,
          isPublic: true,
          isPrivate: false,
          author: {
            id: item.author?.id || "unknown",
            firstName: item.author?.firstName || null,
            lastName: item.author?.lastName || null,
          },
          createdAt: item.createdAt,
          updatedAt: item.createdAt,
        }),
      );

      return {
        texts: mapped,
        pagination: response.pagination,
        append: loadMore,
        filterType,
      };
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : i18n.t("texts:errors.failedToFetchTexts");
      return rejectWithValue(message);
    }
  },
);

// Toggle favorite
export const toggleFavorite = createAsyncThunk<
  { textId: string; favorited: boolean },
  string,
  { rejectValue: string }
>("texts/toggleFavorite", async (textId, { rejectWithValue }) => {
  try {
    const token = getAuthToken();
    const response = await sharedApi.texts.toggleFavorite(textId, token);
    return { textId, favorited: response.favorited };
  } catch (error: unknown) {
    const message =
      error instanceof Error
        ? error.message
        : i18n.t("texts:errors.failedToToggleFavorite");
    return rejectWithValue(message);
  }
});

// Generate text
export const generateText = createAsyncThunk<
  Text,
  GenerateTextRequest,
  { rejectValue: string }
>("texts/generateText", async (params, { rejectWithValue }) => {
  try {
    const token = getAuthToken();
    if (!isLevel(params.level) || !isSupportedLanguage(params.language)) {
      return rejectWithValue("INVALID_MATERIAL_REQUEST");
    }
    const response = await sharedApi.materials.create(
      {
        method: params.method || "AI",
        language: params.language,
        level: params.level,
        contentPrompt: params.topic,
        textSize: params.textSize as "SHORT" | "MEDIUM" | "LONG",
        content: params.content,
        title: params.title,
      },
      token,
    );
    // Map shared API text to legacy Text shape kept in state
    const mapped: Text = {
      id: response.text.id,
      content: response.text.content || "",
      translation: undefined,
      language: response.text.language,
      level: response.text.level,
      textSize: response.text.textSize || params.textSize,
      topic: response.text.topic,
      difficulty: response.text.difficulty,
      voices: [],
      averageRating: response.text.averageRating || 0,
      userRating: 0,
      isFavorited: false,
      isPublic: response.text.isPublic,
      isPrivate: response.text.isPrivate,
      author: {
        id: response.text.author?.id || "unknown",
        firstName: response.text.author?.firstName || null,
        lastName: response.text.author?.lastName || null,
      },
      createdAt: response.text.createdAt || new Date().toISOString(),
      updatedAt:
        response.text.updatedAt ||
        response.text.createdAt ||
        new Date().toISOString(),
    };
    return mapped;
  } catch (error: unknown) {
    const apiError = toApiError(error);
    return rejectWithValue(
      apiError.code || apiError.message || "MATERIAL_TECHNICAL_ERROR",
    );
  }
});

// Submit rating
export const submitRating = createAsyncThunk<
  { textId: string; rating: number },
  { textId: string; rating: number },
  { rejectValue: string }
>("texts/submitRating", async ({ textId, rating }, { rejectWithValue }) => {
  try {
    const token = getAuthToken();
    await sharedApi.texts.rate(textId, rating, token);
    return { textId, rating };
  } catch (error: unknown) {
    const message =
      error instanceof Error
        ? error.message
        : i18n.t("texts:errors.failedToSubmitRating");
    return rejectWithValue(message);
  }
});

// Slice
const textsSlice = createSlice({
  name: "texts",
  initialState,
  reducers: {
    setOwnMaterialFilters: (
      state,
      action: PayloadAction<{ language: string; level: string }>,
    ): void => {
      state.mineFilters = action.payload;
    },
    setFilters: (state, action: PayloadAction<Partial<TextsFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      // Только при активном поиске (length >= 3) очищаем стейт
      if (
        action.payload.search !== undefined &&
        action.payload.search.length >= 3
      ) {
        Object.values(state.textsByFilter).forEach((filterState) => {
          filterState.texts = [];
          filterState.pagination.offset = 0;
          filterState.loadCount = 0;
          filterState.hasMore = false;
        });
      }
    },
    applyFavoriteStatus: (
      state,
      action: PayloadAction<{ textId: string; favorited: boolean }>,
    ) => {
      const { textId, favorited } = action.payload;
      // Обновляем статус во всех фильтрах
      Object.values(state.textsByFilter).forEach((filterState) => {
        const text = filterState.texts.find((t: Text) => t.id === textId);
        if (text) text.isFavorited = favorited;
      });
      const favoritesState = state.textsByFilter.favorites;
      const existsInFavorites = favoritesState.texts.some(
        (t) => t.id === textId,
      );
      if (!favorited && existsInFavorites) {
        favoritesState.texts = favoritesState.texts.filter(
          (t) => t.id !== textId,
        );
        if (favoritesState.pagination.total > 0)
          favoritesState.pagination.total -= 1;
      } else if (favorited && !existsInFavorites) {
        const sourceText =
          state.textsByFilter.all.texts.find((t) => t.id === textId) ||
          state.textsByFilter.mine.texts.find((t) => t.id === textId);
        if (sourceText) {
          favoritesState.texts.unshift(sourceText);
          favoritesState.pagination.total += 1;
        }
      }
    },
    purgeFavoritesGhosts: (state) => {
      const favoritesState = state.textsByFilter.favorites;
      const before = favoritesState.texts.length;
      favoritesState.texts = favoritesState.texts.filter((t) => t.isFavorited);
      const removed = before - favoritesState.texts.length;
      if (removed > 0 && favoritesState.pagination.total >= removed) {
        favoritesState.pagination.total -= removed;
      }
    },
    clearError: (state) => {
      state.error = null;
    },
    clearGenerateError: (state) => {
      state.generateError = "";
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
      // Сбрасываем все состояния фильтров
      Object.values(state.textsByFilter).forEach((filterState) => {
        filterState.texts = [];
        filterState.pagination.offset = 0;
        filterState.loadCount = 0;
        filterState.hasMore = false;
      });
    },
    clearTexts: (state) => {
      // Очищаем только тексты текущего фильтра
      const currentFilter = state.textsByFilter[state.activeFilter];
      currentFilter.texts = [];
      currentFilter.pagination.offset = 0;
      currentFilter.loadCount = 0;
      currentFilter.hasMore = false;
    },
    clearSearchResults: (state) => {
      // Очищаем результаты поиска для всех фильтров
      Object.values(state.textsByFilter).forEach((filterState) => {
        filterState.texts = [];
        filterState.pagination.offset = 0;
        filterState.loadCount = 0;
        filterState.hasMore = false;
      });
    },
    // UI state actions
    setActiveFilter: (
      state,
      action: PayloadAction<"all" | "mine" | "favorites">,
    ) => {
      const prev = state.activeFilter;
      // Если уходим с вкладки избранного – очищаем элементы, у которых уже снят флаг избранного
      if (prev === "favorites" && action.payload !== "favorites") {
        const favoritesState = state.textsByFilter.favorites;
        const before = favoritesState.texts.length;
        favoritesState.texts = favoritesState.texts.filter(
          (t) => t.isFavorited,
        );
        const removed = before - favoritesState.texts.length;
        if (removed > 0 && favoritesState.pagination.total >= removed) {
          favoritesState.pagination.total -= removed;
        }
      }
      state.activeFilter = action.payload;
    },
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
      // Только при активном поиске очищаем стейт
      if (action.payload.length >= 3) {
        Object.values(state.textsByFilter).forEach((filterState) => {
          filterState.texts = [];
          filterState.pagination.offset = 0;
          filterState.loadCount = 0;
          filterState.hasMore = false;
        });
      }
    },
    setIsRandomMode: (state, action: PayloadAction<boolean>) => {
      state.isRandomMode = action.payload;
    },
    setGenerateDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.generateDialogOpen = action.payload;
    },
    setGenerateForm: (
      state,
      action: PayloadAction<Partial<TextsState["generateForm"]>>,
    ) => {
      state.generateForm = { ...state.generateForm, ...action.payload };
    },
    resetGenerateForm: (state) => {
      state.generateForm = initialState.generateForm;
      state.generateDialogOpen = false;
    },
    setUserLanguage: (state, action: PayloadAction<string>) => {
      // Set user's target language in filter options
      state.filterOptions.languages = [action.payload];
    },
    resetTextsState: () => initialState,
  },
  extraReducers: (builder) => {
    // Fetch texts
    builder
      .addCase(fetchTexts.pending, (state, action) => {
        state.textsByFilter[action.meta.arg.filterType || "all"].requestId =
          action.meta.requestId;
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTexts.fulfilled, (state, action) => {
        const { filterType, append, texts, pagination } = action.payload;
        const filterState = state.textsByFilter[filterType];
        if (filterState.requestId !== action.meta.requestId) return;
        state.loading = false;

        if (append) {
          // Append for load more
          filterState.texts = [...filterState.texts, ...texts];
          filterState.loadCount += 1;
        } else {
          // Replace for new search/filter
          filterState.texts = texts;
          filterState.loadCount = 1;
        }

        filterState.pagination = pagination;
        filterState.hasMore = pagination.hasMore;
      })
      .addCase(fetchTexts.rejected, (state, action) => {
        if (
          state.textsByFilter[action.meta.arg.filterType || "all"].requestId !==
          action.meta.requestId
        )
          return;
        state.loading = false;
        state.error =
          action.payload || i18n.t("texts:errors.failedToFetchTexts");
      });

    // Toggle favorite
    builder.addCase(toggleFavorite.fulfilled, (state, action) => {
      const { textId, favorited } = action.payload;

      // Обновляем статус во всех фильтрах, где присутствует текст
      Object.values(state.textsByFilter).forEach((filterState) => {
        const text = filterState.texts.find((t: Text) => t.id === textId);
        if (text) {
          text.isFavorited = favorited;
        }
      });

      // Актуализируем список избранных, но если пользователь сейчас на вкладке 'favorites',
      // НЕ удаляем сразу – оставляем строку (без сердечка) до смены фильтра, чтобы можно было вернуть.
      const favoritesState = state.textsByFilter.favorites;
      const existsInFavorites = favoritesState.texts.some(
        (t) => t.id === textId,
      );
      const onFavoritesTab = state.activeFilter === "favorites";

      if (!favorited && existsInFavorites && !onFavoritesTab) {
        favoritesState.texts = favoritesState.texts.filter(
          (t) => t.id !== textId,
        );
        if (favoritesState.pagination.total > 0)
          favoritesState.pagination.total -= 1;
      } else if (favorited && !existsInFavorites) {
        const sourceText =
          state.textsByFilter.all.texts.find((t) => t.id === textId) ||
          state.textsByFilter.mine.texts.find((t) => t.id === textId) ||
          favoritesState.texts.find((t) => t.id === textId); // fallback
        if (sourceText) {
          favoritesState.texts.unshift(sourceText);
          favoritesState.pagination.total += 1;
        }
      }
    });

    // Generate text
    builder
      .addCase(generateText.pending, (state) => {
        state.generateLoading = true;
        state.generateError = "";
      })
      .addCase(generateText.fulfilled, (state, action) => {
        state.generateLoading = false;
        state.generateDialogOpen = false;

        // Добавляем новый текст в фильтры 'all' и 'mine'
        const newText = action.payload;
        if (
          newText.isPublic &&
          !state.textsByFilter.all.texts.some(
            (text): boolean => text.id === newText.id,
          )
        )
          state.textsByFilter.all.texts.unshift(newText);
        if (
          !state.textsByFilter.mine.texts.some(
            (text): boolean => text.id === newText.id,
          )
        ) {
          state.textsByFilter.mine.texts.unshift(newText);
        }
      })
      .addCase(generateText.rejected, (state, action) => {
        state.generateLoading = false;
        state.generateError =
          action.payload || i18n.t("texts:errors.failedToGenerateText");
      });

    // Submit rating
    builder.addCase(submitPracticeRating.fulfilled, (state, action): void => {
      const { textId, rating, averageRating } = action.payload;
      Object.values(state.textsByFilter).forEach((filterState): void => {
        const text = filterState.texts.find(
          (item): boolean => item.id === textId,
        );
        if (text) {
          text.userRating = rating;
          text.averageRating = averageRating;
        }
      });
    });
    builder.addCase(submitRating.fulfilled, (state, action) => {
      const { textId, rating } = action.payload;

      // Обновляем рейтинг во всех фильтрах
      Object.values(state.textsByFilter).forEach((filterState) => {
        const text = filterState.texts.find((t: Text) => t.id === textId);
        if (text) {
          text.userRating = rating;
        }
      });
    });
  },
});

export const {
  setOwnMaterialFilters,
  setFilters,
  clearError,
  clearGenerateError,
  resetFilters,
  clearTexts,
  clearSearchResults,
  setActiveFilter,
  setSearchTerm,
  setIsRandomMode,
  setGenerateDialogOpen,
  setGenerateForm,
  resetGenerateForm,
  applyFavoriteStatus,
  purgeFavoritesGhosts,
  setUserLanguage,
  resetTextsState,
} = textsSlice.actions;

export default textsSlice.reducer;
